import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../app/store';
import { useParams, useSearchParams } from 'react-router-dom';
import Card from '@mui/material/Card';
import { Box, CardContent, CircularProgress, Grid } from '@mui/material';
import logo from "../images/edgrologo.png";
import verifiedLogo from "../images/Tick.svg";
import failedLogo from "../images/failedLogo.svg";
import "../styles/DigitalKyc.scss";
import {
    DigitalKycState,
    validateDkycRequestIdAsync,
    getAccessTokenForDkycAsync,
    getDigitalKycDataAsync,
    resetValidateDkycFlag,
    resetgetDigitalKycDataFlag
} from "../redux/DigitalKycSlice";
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { VideoKycRequestStatus } from '../../../apis/models/VideoKycRequestStatus';


// Define props interfaces
interface StateProps {
    isLoading: boolean;
}

interface DispatchProps {
    updateKycStatus: (status: string) => void;
}

type Props = StateProps & DispatchProps;
type HyperKycResult = {
    status: string;
}

const DigitalKyc: React.FC<Props> = () => {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const successUrl = searchParams.get("dkycsuccessURL");
    const failureUrl = searchParams.get("dkycFailureURL");
    const [countDown, setCountdown] = useState(0);
    const [sdkOpen, setSdkOpen] = useState(false);
    const requestId = params.RequestId;
    const { loading, error, hypervergeAccessToken, digitalKycData, sourceOfDigitalKycData, doneActions } =
        useAppSelector((state: RootState): DigitalKycState => state.digitalKyc);
    const dispatch = useAppDispatch();
    
    useEffect(() => {
        dispatch(validateDkycRequestIdAsync(requestId as string))
    }, [])

    const redirectToOneFin = (status: VideoKycRequestStatus) => {
        
        const successStatuses = [
            VideoKycRequestStatus.DONE,
            VideoKycRequestStatus.USER_ACTION_COMPLETED,
            VideoKycRequestStatus.KYC_DATA_AVAILABLE
        ].includes(status); 

        const failureStatuses = [
            VideoKycRequestStatus.FAILED,
            VideoKycRequestStatus.INITIATE_FAILED
        ].includes(status); 
        if (
            successStatuses || failureStatuses
        ) {
            let timer: NodeJS.Timeout;
            setCountdown(5);
            timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev === 1) {
                        clearInterval(timer);
                        if(successUrl || failureUrl)
                        {
                            window.location.replace(successStatuses ? successUrl as string : failureUrl as string);
                        }
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }

    useEffect(() => {
        if(doneActions.validateDkycRequestId)
        {
            if (!digitalKycData || !digitalKycData.requestDetails) return;

            const status = digitalKycData.requestDetails.Status;
            if (
                [
                    VideoKycRequestStatus.CREATED, 
                    VideoKycRequestStatus.INITIATE_SUCCESS,
                    VideoKycRequestStatus.FAILED
                ].includes(status)
            ) {
                dispatch(getAccessTokenForDkycAsync(requestId as string));
            }
            const canRedirectToOnefin = [
                VideoKycRequestStatus.DONE,
                VideoKycRequestStatus.USER_ACTION_COMPLETED,
                VideoKycRequestStatus.KYC_DATA_AVAILABLE
            ].includes(status); 

            if(canRedirectToOnefin) redirectToOneFin(status);

            dispatch(resetValidateDkycFlag())
        }

    }, [doneActions.validateDkycRequestId]);

    useEffect(() => {
        if(doneActions.getDigitalKycData)
        {
            if (!digitalKycData || !digitalKycData.requestDetails) return;

            const status = digitalKycData.requestDetails.Status;

            redirectToOneFin(status);
            
            dispatch(resetgetDigitalKycDataFlag())
        }
    }, [doneActions.getDigitalKycData]);



    useEffect(() => {
        if (hypervergeAccessToken) {
            const workflowId = "offline_flow";

            const hyperKycConfig = new window.HyperKycConfig(hypervergeAccessToken, workflowId, requestId);
            const customInputs = {
                Watermark_text: "Done by Edgro"
            };
            hyperKycConfig.setInputs(customInputs);
            hyperKycConfig.setUseLocation(true);
            window.HyperKYCModule.launch(hyperKycConfig, handler);
            setSdkOpen(true);
        }
    }, [hypervergeAccessToken])


    const handler = (HyperKycResult: HyperKycResult) => {
        const status = HyperKycResult.status;

        switch (status) {
            case "user_cancelled":
            case "error":
            case "auto_approved":
            case "auto_declined":
            case "needs_review":
                dispatch(getDigitalKycDataAsync(requestId as string));
                setSdkOpen(false);
                break;
            default:
                break;
        }
    };

    return (

        <React.Fragment>
            <Grid className="header">
                <img alt="logo" src={logo} className="edgro-logo"></img>
            </Grid>
            <Grid
                container
                spacing={2}
                direction="column"
                alignItems="center"
                justifyContent="center"
                className="dkyc-container"
            >
                <Card raised={true} className="result-card">
                    <CardContent className="card-content">

                        {(loading.validateDkycRequestId == "loading" ||
                            loading.getAccessTokenForDkyc == "loading" ||
                            loading.getDigitalKycData == "loading"
                        ) && (
                                <>

                                    <CircularProgress />
                                    <span className="card-details">
                                        {loading.validateDkycRequestId == "loading" && (
                                            <h1>Validating your request...</h1>
                                        )}
                                        {loading.getAccessTokenForDkyc == "loading" && (
                                            <h1>Fetching Access token...</h1>
                                        )}
                                        {loading.getDigitalKycData == "loading" && (
                                            <h1>Fetching Your request...</h1>
                                        )}
                                        <p>
                                            Your Digital KYC verification is in progress.
                                        </p>
                                    </span>
                                </>
                            )}


                        {
                            ["validateDkycRequestId", "getDigitalKycData"].includes(sourceOfDigitalKycData) && 
                            digitalKycData?.requestDetails && [
                            VideoKycRequestStatus.DONE,
                            VideoKycRequestStatus.USER_ACTION_COMPLETED,
                            VideoKycRequestStatus.KYC_DATA_AVAILABLE
                            ].includes(digitalKycData?.requestDetails.Status) && (
                                <React.Fragment>
                                    <Box className="verified-logo-container">
                                        <img
                                            alt="verified"
                                            src={verifiedLogo}
                                            className="verified-logo"
                                        ></img>
                                    </Box>
                                    <span className="card-details">
                                        <h1>Thank you!!</h1>
                                        <p>
                                            Your Digital KYC verification is done.
                                        </p>
                                        <p>
                                            You will be redirected back to Onefin in {countDown}{" "}
                                            seconds.
                                        </p>
                                    </span>

                                </React.Fragment>
                            )}

                        {sdkOpen && (
                                <React.Fragment>
                                    <Box className="verified-logo-container">
                                        <CircularProgress />
                                    </Box>
                                    <span className="card-details">
                                        <p>
                                            Your Digital KYC verification is in progress.
                                        </p>
                                    </span>

                                </React.Fragment>
                            )}

                        {((digitalKycData?.requestDetails && sourceOfDigitalKycData == "getDigitalKycData" &&
                            [VideoKycRequestStatus.FAILED, VideoKycRequestStatus.INITIATE_FAILED].includes(
                                digitalKycData.requestDetails.Status
                            )) || error) && (
                                <React.Fragment>
                                    <Box className="failed-logo-container">
                                        <img
                                            alt="failed"
                                            src={failedLogo}
                                            className="failed-logo"
                                        />
                                    </Box>
                                    <span className="card-details">
                                        <h1>Oops!</h1>
                                        <p>
                                            We couldn’t complete your Digital KYC verification.
                                        </p>
                                        <p>
                                            You will be redirected back to Onefin in {countDown}{" "}
                                            seconds.
                                        </p>
                                    </span>
                                </React.Fragment>

                            )}

                        {(digitalKycData && !digitalKycData?.requestDetails) && (
                            <React.Fragment>
                                <Box className="failed-logo-container">
                                    <img
                                        alt="failed"
                                        src={failedLogo} // Make sure you have a failed logo icon imported
                                        className="failed-logo"
                                    />
                                </Box>
                                <span className="card-details">
                                    <h1>Oops!</h1>
                                    <p>
                                        Invalid Request.
                                    </p>
                                    <p>
                                            We couldn’t complete your Digital KYC verification.
                                    </p>
                                </span>
                            </React.Fragment>

                        )}
                    </CardContent>
                </Card>

            </Grid>
        </React.Fragment>
    )
};

// mapStateToProps function
const mapStateToProps = (state: RootState): StateProps => ({
    isLoading: false,
});

// mapDispatchToProps object
const mapDispatchToProps: DispatchProps = {
    updateKycStatus: (status: string) => ({ type: 'UPDATE_KYC_STATUS', payload: status }),
};

// Connect and export
export default connect<StateProps, DispatchProps, {}, RootState>(
    mapStateToProps,
    mapDispatchToProps
)(DigitalKyc);